import classNames from 'classnames';

import { useSidenavStore } from 'hooks/useSidenavStore';
import { TopNavigationBar, Sidenav } from 'presentation/components';
import './Layout.css';
import Footer from 'presentation/components/Footer/Footer';
import MobileLayout from 'presentation/components/Mobile/MobileLayout';
import { LayoutProvider } from 'context/LayoutContext';
import useIsMobile from 'hooks/useIsMobile';
import { useClearReportOnNavigation } from 'hooks/useClearReportOnNavigation';
import { useEffect } from 'react';
import { runAIAnalysisCacheHousekeepingOnce } from 'utils/cacheUtils';
import { logger } from 'presentation/components/KSExport/reportNTParser/utils';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  children: React.ReactNode;
  grayBg?: boolean;
  noPadding?: boolean;
  noFooter?: boolean;
};

function Layout({ children, grayBg = true, noPadding = false, noFooter = false }: Props): JSX.Element {
  const { isSidenavCollapsed } = useSidenavStore();
  const isMobile = useIsMobile(); // Uses both user agent and viewport width
  const { enableQueueV2 } = useFlags();

  // Clear currentReport when navigating to non-report pages
  useClearReportOnNavigation();

  // Run cache housekeeping once per session at app root
  useEffect(() => {
    try {
      const result = runAIAnalysisCacheHousekeepingOnce();
      const isAllCacheItemDeleted =
        result.legacyAIAnalysisRemoved ||
        result.legacyPromptVersionsRemoved ||
        result.legacyAIEditorResponsesRemoved ||
        result.expiredRemoved;
      if (result.ran && isAllCacheItemDeleted) {
        logger.msg(
          `[CacheHousekeeping] removed: AI Analysis legacy=${result.legacyAIAnalysisRemoved}, Prompt Versions legacy=${result.legacyPromptVersionsRemoved}, AI Editor Responses legacy=${result.legacyAIEditorResponsesRemoved}, expired=${result.expiredRemoved}`
        );
      }
    } catch (error) {
      console.error('Cache housekeeping failed', error);
      Sentry.captureException(error);
    }
  }, []);

  // If on mobile with new KSQ2 layout enabled, render children directly
  // Dashboard will handle its own mobile layout (MobileKSQ2Layout)
  if (isMobile && enableQueueV2) {
    return <LayoutProvider>{children}</LayoutProvider>;
  }

  // If on mobile without KSQ2, render the legacy mobile layout
  if (isMobile) {
    return (
      <LayoutProvider>
        <MobileLayout>{children}</MobileLayout>
      </LayoutProvider>
    );
  }

  // Otherwise render the desktop layout
  return (
    <div className="Layout d-print-block">
      <nav className={classNames('Layout-nav', { 'Layout-nav_open': isSidenavCollapsed })}>
        <Sidenav />
      </nav>
      <main className="Layout-main">
        <div className={classNames('Layout-content', { 'no-footer': noFooter })}>{children}</div>
        {!noFooter && <Footer />}
      </main>
    </div>
  );
}

export default Layout;
